import { useSecureGraphqlMutation, type TFunction } from '@psyomics/ui-common';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ContactRespondentType, CsvParseError, ParsedReferralUploadRow, parseReferralUploadCsv } from './csv-parser';
import {
  CreateReferralUploadJobDocument,
  CreateReferralUploadJobMutation,
  CreateReferralUploadJobMutationVariables,
  ReferralUploadRowInput,
  RespondentType,
} from './generated/referral-upload-jobs';
import { UploadService } from './types';

type UploadResult = CreateReferralUploadJobMutation['createReferralUploadJob'];
type UploadOutcome = { success: boolean };
type ReadFileResult = { content: string; error?: undefined } | { content?: undefined; error: string };

const MAX_FILE_SIZE_MB = 5;
const BYTES_PER_MB = 1048576;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * BYTES_PER_MB;
const MAX_FILE_NAME_LENGTH = 255;

const isValidationError = (error: { graphQLErrors: { extensions?: Record<string, unknown> }[] }) => {
  const extensions = error.graphQLErrors[0]?.extensions;
  return extensions?.['isUserFacing'] === true && extensions?.['messageKey'] === 'ValidationError';
};

const INCOMPLETE_CONTACT_ROW_KEY = {
  carer: 'incompleteCarerRow',
  informant1: 'incompleteInformant1Row',
} as const satisfies Record<ContactRespondentType, string>;

const translateParseError = (t: TFunction, error: CsvParseError, serviceDescription: string): string => {
  switch (error.key) {
    case 'incompleteData': {
      const rowMessages = error.rows.map((row) =>
        t('page.referralUploadJobs.errors.incompleteRow', { columns: row.columns, rowNumber: row.rowNumber })
      );
      const contactRowMessages = error.contactRows.map((row) =>
        t(`page.referralUploadJobs.errors.${INCOMPLETE_CONTACT_ROW_KEY[row.respondentType]}`, {
          columns: row.columns,
          rowNumber: row.rowNumber,
        })
      );
      return [...rowMessages, ...contactRowMessages].join('; ');
    }
    case 'fieldsTooLong':
      return error.rows
        .map((row) =>
          t('page.referralUploadJobs.errors.fieldTooLongRow', {
            columns: row.columns,
            max: error.max,
            rowNumber: row.rowNumber,
          })
        )
        .join('; ');
    case 'invalidPhoneNumbers':
      return error.rows
        .map((row) =>
          t('page.referralUploadJobs.errors.invalidPhoneNumberRow', { columns: row.columns, rowNumber: row.rowNumber })
        )
        .join('; ');
    case 'duplicateReferrals':
      return error.rows
        .map((row) =>
          t('page.referralUploadJobs.errors.duplicateReferralRow', {
            eprId: row.eprId,
            rowNumber: row.rowNumber,
            service: serviceDescription,
          })
        )
        .join('; ');
    case 'noDataRows':
    case 'emptyColumnHeader':
      return t(`page.referralUploadJobs.errors.${error.key}`);
    default:
      return t(`page.referralUploadJobs.errors.${error.key}`, error.params);
  }
};

const RESPONDENT_TYPE: Record<ContactRespondentType, RespondentType> = {
  carer: RespondentType.Carer,
  informant1: RespondentType.Informant1,
};

const toRowInput = (row: ParsedReferralUploadRow): ReferralUploadRowInput => ({
  ...row,
  contacts: row.contacts?.map((contact) => ({ ...contact, respondentType: RESPONDENT_TYPE[contact.respondentType] })),
});

const readFileContent = async (file: File, t: TFunction): Promise<ReadFileResult> => {
  if (file.name.trim().length > MAX_FILE_NAME_LENGTH) {
    return { error: t('page.referralUploadJobs.errors.fileNameTooLong', { max: MAX_FILE_NAME_LENGTH }) };
  }

  if (file.size > MAX_FILE_SIZE_BYTES) {
    return { error: t('page.referralUploadJobs.errors.fileTooLarge', { maxSizeMb: MAX_FILE_SIZE_MB }) };
  }

  try {
    return { content: await file.text() };
  } catch {
    return { error: t('page.referralUploadJobs.errors.fileReadError') };
  }
};

const useUploadReferralCsv = ({ code: serviceCode, description: serviceDescription }: UploadService) => {
  const { t } = useTranslation();
  const [{ fetching: uploading }, executeUpload] = useSecureGraphqlMutation<
    CreateReferralUploadJobMutation,
    CreateReferralUploadJobMutationVariables,
    'ValidationError'
  >(CreateReferralUploadJobDocument, ['ValidationError' as const]);
  const [result, setResult] = useState<UploadResult | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const uploadFile = useCallback(
    async (file: File): Promise<UploadOutcome> => {
      setResult(null);
      setErrorMessage(null);

      const { content, error: readError } = await readFileContent(file, t);
      if (readError || !content) {
        setErrorMessage(readError ?? t('page.referralUploadJobs.errors.fileReadError'));
        return { success: false };
      }

      const { parseError, rows } = parseReferralUploadCsv(content);

      if (parseError) {
        setErrorMessage(translateParseError(t, parseError, serviceDescription));
        return { success: false };
      }

      const response = await executeUpload({
        input: { fileName: file.name, rows: rows.map(toRowInput), serviceCode },
      });

      if (response.error) {
        if (isValidationError(response.error)) {
          setErrorMessage(t('page.referralUploadJobs.errors.validationFailed'));
        }
        return { success: false };
      }

      if (!response.data) return { success: false };

      setResult(response.data.createReferralUploadJob);
      return { success: true };
    },
    [executeUpload, serviceCode, serviceDescription, t]
  );

  const clearResult = useCallback(() => {
    setResult(null);
    setErrorMessage(null);
  }, []);

  return { clearResult, errorMessage, result, uploadFile, uploading };
};

export { useUploadReferralCsv };
